import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { AnswerBox } from '@/components';
import { Assessment } from '@/types';

interface QuestionContentProps {
  assessment?: Assessment;
  answers: (number | null)[];
  currentQuestionIndex: number;
  isLocked?: boolean;
  setAnswer({
    questionIndex,
    value,
  }: {
    questionIndex: number;
    value: number;
  }): void;
  nextQuestion(): void;
}

export const QuestionContent = ({
  assessment,
  answers,
  currentQuestionIndex,
  isLocked,
  setAnswer,
  nextQuestion,
}: QuestionContentProps) => {
  const [isSwitchingQuestions, setIsSwitchingQuestions] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const onNextQuestion = () => {
    if (!isLocked && !isSwitchingQuestions) {
      setIsSwitchingQuestions(true);
      setTimeout(() => {
        nextQuestion();

        // Add an extra delay to allow the next question to render before allowing the user to answer
        setTimeout(() => {
          setIsSwitchingQuestions(false);
        }, 100);
      }, 100);
    }
  };

  // If question is being answered, keep focus on the next question that appears
  useEffect(() => {
    const hasFocus = container.current?.matches(':focus-within');
    if (hasFocus) {
      container.current
        ?.querySelector('input:not([tabindex="-1"])')
        ?.closest('label')
        ?.focus();
    }
  }, [currentQuestionIndex]);
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        borderTop: '1px solid',
        borderColor: 'grey100',
      }}
      ref={container}>
      {assessment?.questions.map((question, index) => (
        <Box
          key={question.userAssessmentQuestionId}
          sx={[
            styles.questionSlide,
            {
              transform: {
                xs: `translateX(${(index - currentQuestionIndex) * 5}%)`,
                md: `translateY(${(index - currentQuestionIndex) * 5}%)`,
              },
            },
            currentQuestionIndex === index && styles.activeQuestionSlide,
          ]}>
          <Typography variant="caption1" sx={{ mb: 1 }}>
            Question {index + 1}
          </Typography>
          <Typography variant="body2SemiBold" sx={{ mb: { xs: 2, md: 3 } }}>
            {question.question}
          </Typography>
          <Box sx={styles.answerGrid}>
            {question.answers.map((answer, answerIndex) => (
              <Box
                key={answer.assessmentAnswerId}
                onMouseUp={onNextQuestion}
                onKeyUp={e => {
                  if (answers[index] && (e.key === 'Enter' || e.key === ' ')) {
                    onNextQuestion();
                  }
                }}>
                <AnswerBox
                  onSelect={value => {
                    setAnswer({
                      questionIndex: index,
                      value: value as number,
                    });
                  }}
                  isSelected={
                    !isLocked && answers[index] === answer.assessmentAnswerId
                  }
                  answer={answer}
                  index={answerIndex}
                  isDisabled={isLocked}
                  isLocked={currentQuestionIndex !== index || isLocked}
                  questionId={question.userAssessmentQuestionId}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const styles = {
  questionSlide: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    p: { xs: 1, md: 4 },
    opacity: 0,
    transitionProperty: 'opacity, transform',
    transitionDuration: '0.3s',
    pointerEvents: 'none',
  },
  activeQuestionSlide: {
    opacity: 1,
    transform: 'translateY(0)',
    zIndex: 1,
    pointerEvents: 'auto',
  },
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: { sm: '1fr 1fr' },
    gap: 1,
    mt: 1,
    maxWidth: 800,
  },
};
